import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { UserPlus, Copy, Check, ShieldCheck, KeyRound, Trash2, X, Mail, ArrowUpRight, AlertCircle, Upload, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLORS = {
  admin: "#EF4444",
  executive: "#D4AF37",
  consultant: "#22C55E",
  viewer: "#94A3B8",
};

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", title: "", role: "consultant", is_org_admin: false });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/team/members");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Only the organization administrator can manage team members");
      }
    }
  };
  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    if (!form.email || !form.name) {
      toast.error("Email and name are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/team/invite", form);
      setCredentials(r.data);
      setInviteOpen(false);
      setForm({ email: "", name: "", title: "", role: "consultant", is_org_admin: false });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  const resetPassword = async (memberId) => {
    if (!window.confirm("Generate a new password for this user? Their current password will stop working.")) return;
    try {
      const r = await api.post(`/team/members/${memberId}/reset-password`);
      setCredentials({
        member: { email: r.data.login_email, name: data.members.find((m) => m.id === memberId)?.name || "" },
        temporary_password: r.data.temporary_password,
        login_email: r.data.login_email,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const remove = async (memberId) => {
    if (!window.confirm("Remove this team member? They will lose access immediately.")) return;
    try {
      await api.delete(`/team/members/${memberId}`);
      toast.success("Member removed");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove member");
    }
  };

  const toggleAdmin = async (m) => {
    try {
      await api.patch(`/team/members/${m.id}`, { is_org_admin: !m.is_org_admin });
      toast.success(m.is_org_admin ? "Removed admin rights" : "Granted admin rights");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (!data) {
    return (
      <div className="p-10 text-slate-400 font-mono text-xs tracking-widest">
        {user?.is_org_admin || user?.role === "admin" ? "// LOADING TEAM…" : "// ACCESS RESTRICTED — Org administrators only"}
      </div>
    );
  }

  const org = data.organization;
  const isInternal = org.suite === "internal";
  const used = data.seats_used;
  const limit = isInternal ? "∞" : data.seats_limit;
  const pct = isInternal ? 0 : Math.min(100, (used / data.seats_limit) * 100);
  const atLimit = !isInternal && used >= data.seats_limit;

  return (
    <div className="p-8 space-y-6 max-w-6xl mx-auto" data-testid="team-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Team & Seats</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Institutional Team</h1>
          <p className="text-slate-400 mt-2 text-sm">
            Invite staff under <span className="text-white">{org.name}</span>. Each member gets a secure password and full
            access to the modules included in your subscribed suite.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCsvOpen(true)}
            disabled={atLimit}
            data-testid="csv-import-btn"
            className="px-5 py-2.5 flex items-center gap-2 text-sm font-medium border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" /> Import CSV
          </button>
          <button
            onClick={() => setInviteOpen(true)}
            disabled={atLimit}
            data-testid="invite-member-btn"
            className="px-5 py-2.5 flex items-center gap-2 text-sm font-medium disabled:opacity-50"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
          >
            <UserPlus className="w-4 h-4" /> Invite member
          </button>
        </div>
      </div>

      {/* Seat usage */}
      <div className="nexus-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-eyebrow">Seat allocation</div>
            <div className="font-mono text-2xl text-white mt-1">{used} / {limit}</div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Suite</div>
            <div className="font-mono text-sm text-[#D4AF37] uppercase mt-1">{org.suite}</div>
          </div>
        </div>
        {!isInternal && (
          <div className="h-1 bg-[#10182B]">
            <div className="h-full" style={{ width: `${pct}%`, background: pct > 90 ? "#EF4444" : "var(--gold-500)" }}></div>
          </div>
        )}
        {atLimit && (
          <div className="mt-4 flex items-start gap-2 text-xs text-red-400">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <span>Seat limit reached. <Link to="/pricing" className="underline">Upgrade your suite</Link> to add more team members.</span>
          </div>
        )}
      </div>

      {/* Members */}
      <div>
        <div className="label-eyebrow mb-3">// Members ({data.members.length})</div>
        <div className="nexus-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                {["Member", "Email", "Role", "Joined", "Actions"].map((h) => (
                  <th key={h} className="px-5 py-3 text-left label-eyebrow">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id} className="border-b border-white/5" data-testid={`member-row-${m.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center font-mono text-xs"
                        style={{ background: "var(--bg-surface-2)", color: "var(--gold-400)", border: "1px solid var(--border-default)" }}>
                        {(m.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("")}
                      </div>
                      <div>
                        <div className="text-sm text-white">{m.name}</div>
                        <div className="text-[11px] text-slate-500">{m.title || "—"}</div>
                      </div>
                      {m.is_org_admin && (
                        <span className="px-1.5 py-0.5 font-mono text-[9px] tracking-widest" style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37" }}>
                          ADMIN
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-300 font-mono text-xs">{m.email}</td>
                  <td className="px-5 py-3">
                    <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: ROLE_COLORS[m.role] || "#94A3B8" }}>
                      {m.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs font-mono text-slate-500">{new Date(m.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleAdmin(m)} title={m.is_org_admin ? "Demote" : "Promote to admin"}
                        className="p-1.5 text-slate-500 hover:text-[#D4AF37]" data-testid={`toggle-admin-${m.id}`}>
                        <ShieldCheck className="w-4 h-4" />
                      </button>
                      <button onClick={() => resetPassword(m.id)} title="Reset password"
                        className="p-1.5 text-slate-500 hover:text-[#D4AF37]" data-testid={`reset-pw-${m.id}`}>
                        <KeyRound className="w-4 h-4" />
                      </button>
                      {m.id !== user.id && (
                        <button onClick={() => remove(m.id)} title="Remove"
                          className="p-1.5 text-slate-500 hover:text-red-400" data-testid={`remove-${m.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {data.members.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-slate-500">No team members yet. Invite your first staff.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite modal */}
      {inviteOpen && (
        <Modal onClose={() => setInviteOpen(false)} title="Invite team member">
          <div className="space-y-4">
            <Field label="Full name *">
              <input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pr-input" placeholder="e.g., Yaa Asantewaa" />
            </Field>
            <Field label="Company email *">
              <input data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pr-input" placeholder="staff@yourcompany.com" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Title">
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="pr-input" placeholder="e.g., Senior Analyst" />
              </Field>
              <Field label="Role">
                <select data-testid="invite-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="pr-input">
                  <option value="consultant">Consultant</option>
                  <option value="executive">Executive</option>
                  <option value="viewer">Viewer (read-only)</option>
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
              <input type="checkbox" checked={form.is_org_admin} onChange={(e) => setForm({ ...form, is_org_admin: e.target.checked })} />
              Grant organization-admin rights (can invite & manage other members)
            </label>
            <div className="text-[11px] text-slate-500 leading-relaxed">
              A secure 12-character password will be generated. Share it privately with the staff member — they can change it
              after first login. All work created under this account remains accessible only to organization admins.
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setInviteOpen(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="invite-submit" className="px-5 py-2 font-medium disabled:opacity-50" style={{ background: "var(--gold-500)", color: "#050B14" }}>
              {busy ? "Inviting…" : "Generate credentials"}
            </button>
          </div>
        </Modal>
      )}

      {/* Credentials modal (shows password ONCE) */}
      {credentials && (
        <Modal onClose={() => { setCredentials(null); setCopied(false); }} title="Credentials generated">
          <div className="text-sm text-slate-300 mb-4">
            Share these credentials securely with <span className="text-white font-medium">{credentials.member.name || credentials.login_email}</span>.
            The password will not be displayed again.
          </div>
          <div className="space-y-3">
            <CredBox label="Login email" value={credentials.login_email} icon={<Mail className="w-4 h-4" />} />
            <CredBox label="Temporary password" value={credentials.temporary_password} icon={<KeyRound className="w-4 h-4" />} mono />
            <div
              onClick={() => {
                navigator.clipboard.writeText(`Login: ${credentials.login_email}\nPassword: ${credentials.temporary_password}`);
                setCopied(true);
                toast.success("Copied to clipboard");
                setTimeout(() => setCopied(false), 2000);
              }}
              data-testid="copy-creds-btn"
              className="cursor-pointer p-3 border border-[#D4AF37]/30 bg-[#D4AF37]/5 flex items-center justify-center gap-2 text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy both</>}
            </div>
          </div>
          <div className="mt-5 text-[11px] text-slate-500 leading-relaxed">
            The user should sign in at <span className="text-white">/login</span> with these credentials and change their password
            from Governance &gt; Settings on first login.
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={() => { setCredentials(null); setCopied(false); }} className="px-5 py-2 font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>
              Done
            </button>
          </div>
        </Modal>
      )}

      {/* CSV Import modal */}
      {csvOpen && (
        <CsvImportModal
          onClose={() => setCsvOpen(false)}
          seatsRemaining={Math.max(0, limit - used)}
          onDone={() => { setCsvOpen(false); refresh(); }}
        />
      )}

      <style>{`.pr-input { width:100%; padding:0.5rem 0.75rem; background:#050B14; border:1px solid rgba(255,255,255,0.1); color:#F8FAFC; font-family:'IBM Plex Mono', monospace; font-size:0.875rem; outline:none; } .pr-input:focus { border-color:#D4AF37; }`}</style>
    </div>
  );
}

const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-xl max-h-[90vh] overflow-y-auto relative"
      onClick={(e) => e.stopPropagation()}
    >
      <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
      <div className="label-eyebrow mb-1">// Pandora Nexus</div>
      <h2 className="font-display text-2xl text-white mb-5">{title}</h2>
      {children}
    </motion.div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);

const CredBox = ({ label, value, icon, mono }) => (
  <div className="p-3 border border-white/10 bg-[#050B14]">
    <div className="flex items-center gap-2 label-eyebrow mb-1">{icon} {label}</div>
    <div className={`text-white ${mono ? "font-mono text-base tracking-wider" : "text-sm"} break-all`}>{value}</div>
  </div>
);

// ---------- CSV Import Modal ----------
// Client-side CSV parsing (RFC 4180 subset: handles quoted commas + escaped quotes).
function parseCsv(text) {
  const rows = [];
  let cur = [""], inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { cur[cur.length - 1] += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else { cur[cur.length - 1] += c; }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") cur.push("");
      else if (c === "\n" || c === "\r") {
        if (c === "\r" && text[i + 1] === "\n") i++;
        rows.push(cur); cur = [""];
      } else { cur[cur.length - 1] += c; }
    }
  }
  if (cur.length > 1 || cur[0] !== "") rows.push(cur);
  return rows;
}

const REQUIRED = ["email", "name"];
const OPTIONAL = ["title", "department", "role"];

function CsvImportModal({ onClose, seatsRemaining, onDone }) {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result).trim());
      if (parsed.length < 2) {
        toast.error("CSV needs a header row and at least one data row");
        return;
      }
      const hdrs = parsed[0].map((h) => h.trim().toLowerCase());
      const missing = REQUIRED.filter((r) => !hdrs.includes(r));
      if (missing.length) {
        toast.error(`Missing required column(s): ${missing.join(", ")}`);
        return;
      }
      const data = parsed.slice(1).map((r) => {
        const obj = {};
        hdrs.forEach((h, i) => { obj[h] = (r[i] ?? "").trim(); });
        return obj;
      }).filter((r) => r.email);
      setHeaders(hdrs);
      setRows(data);
      setResult(null);
    };
    reader.readAsText(f);
  };

  const submit = async () => {
    if (rows.length === 0) { toast.error("Add rows first"); return; }
    if (rows.length > seatsRemaining) {
      toast.error(`${rows.length} rows won't fit — only ${seatsRemaining} seat(s) remaining. Upgrade your plan or trim the file.`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/team/bulk-invite", { rows });
      setResult(r.data);
      if (r.data.created > 0) {
        toast.success(`Invited ${r.data.created} of ${r.data.total} — ${r.data.failed} skipped`);
      } else {
        toast.error(`All ${r.data.total} rows skipped — see details below`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Bulk invite failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = "email,name,title,department,role\nyaa@example.com,Yaa Asantewaa,Senior Analyst,Strategy,consultant\nkofi@example.com,Kofi Owusu,Director,Operations,executive\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "pandora-team-template.csv";
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="csv-modal">
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="bg-[#0A111F] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <div className="label-eyebrow">// Bulk Import</div>
            <h2 className="font-display text-xl text-white mt-1">Import team from CSV</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4">
          {!result && (
            <>
              <div className="text-xs text-slate-400 leading-relaxed">
                Upload a CSV with columns: <span className="text-[#D4AF37] font-mono">email, name</span> (required) and optionally <span className="font-mono">title, department, role</span>. Each row becomes an invited staff member with an auto-generated password emailed to them.
              </div>

              <div className="flex gap-2 flex-wrap">
                <label className="cursor-pointer px-4 py-2.5 flex items-center gap-2 text-sm font-medium border border-white/10 hover:border-[#D4AF37]/40 text-white" data-testid="csv-upload-label">
                  <Upload className="w-4 h-4" /> Choose CSV file
                  <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="csv-file-input" />
                </label>
                <button onClick={downloadTemplate} data-testid="csv-download-template" className="px-4 py-2.5 flex items-center gap-2 text-sm text-slate-400 hover:text-white border border-white/5">
                  <FileText className="w-4 h-4" /> Download template
                </button>
              </div>

              {fileName && (
                <div className="text-xs text-slate-500 font-mono">Loaded · <span className="text-white">{fileName}</span> · {rows.length} row(s), {headers.length} column(s)</div>
              )}

              {rows.length > 0 && (
                <>
                  <div className="border border-white/5 max-h-64 overflow-y-auto" data-testid="csv-preview">
                    <table className="w-full text-xs">
                      <thead className="bg-[#050B14] sticky top-0">
                        <tr>
                          {[...REQUIRED, ...OPTIONAL].filter((c) => headers.includes(c)).map((c) => (
                            <th key={c} className="px-3 py-2 text-left font-mono text-[10px] text-slate-500 uppercase tracking-widest">{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0, 50).map((r, i) => (
                          <tr key={i} className="border-t border-white/5">
                            {[...REQUIRED, ...OPTIONAL].filter((c) => headers.includes(c)).map((c) => (
                              <td key={c} className="px-3 py-2 text-slate-300">{r[c] || <span className="text-slate-600">—</span>}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length > 50 && <div className="p-2 text-[10px] text-slate-500 text-center border-t border-white/5">…and {rows.length - 50} more rows</div>}
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[#050B14] border border-white/5">
                    <div className="text-xs text-slate-400">
                      <span className="text-white font-mono">{rows.length}</span> to invite · <span className="text-white font-mono">{seatsRemaining}</span> seat(s) remaining
                    </div>
                    <button
                      onClick={submit}
                      disabled={busy || rows.length === 0}
                      data-testid="csv-submit"
                      className="px-5 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-40"
                      style={{ background: "var(--gold-500)", color: "#050B14" }}
                    >
                      {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Inviting…</> : `Invite ${rows.length} member${rows.length === 1 ? "" : "s"}`}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {result && (
            <div className="space-y-3" data-testid="csv-result">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 border border-emerald-500/20 bg-emerald-500/5 text-center">
                  <div className="font-display text-2xl text-emerald-400">{result.created}</div>
                  <div className="label-eyebrow mt-1">CREATED</div>
                </div>
                <div className="p-3 border border-amber-500/20 bg-amber-500/5 text-center">
                  <div className="font-display text-2xl text-amber-400">{result.failed}</div>
                  <div className="label-eyebrow mt-1">SKIPPED</div>
                </div>
                <div className="p-3 border border-white/10 text-center">
                  <div className="font-display text-2xl text-white">{result.total}</div>
                  <div className="label-eyebrow mt-1">TOTAL</div>
                </div>
              </div>
              <div className="border border-white/5 max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#050B14] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-mono text-[10px] text-slate-500 uppercase tracking-widest">Status</th>
                      <th className="px-3 py-2 text-left font-mono text-[10px] text-slate-500 uppercase tracking-widest">Email</th>
                      <th className="px-3 py-2 text-left font-mono text-[10px] text-slate-500 uppercase tracking-widest">Reason / Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="px-3 py-2">
                          {r.ok ? <span className="text-emerald-400 font-mono text-[10px]">✓ INVITED</span>
                                : <span className="text-amber-400 font-mono text-[10px]">✗ SKIPPED</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-300 font-mono">{r.email}</td>
                        <td className="px-3 py-2 text-slate-400">
                          {r.ok ? (
                            r.email_delivery?.ok ? <span className="text-emerald-400/80">email sent</span>
                                                  : <span className="text-amber-400/80">created, email failed: {r.email_delivery?.reason || "?"}</span>
                          ) : (r.reason || "unknown")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <button onClick={onDone} className="px-5 py-2 font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

